import { gql, useQuery } from '@apollo/client'
import { ForbiddenError } from '../components/ErrorBoundary'
import { StructParams, StructureBase, getChildType, QueryStructParams, VDCounts } from '../lib/structures'
import type { Timeline } from '../types/Timeline'
import { useEffect } from 'react'

interface CanvassingDataForChildren {
  current: number
  last7days: number
  last30days: number
  weeklyAvg: number
  weeklyAvgRequired: number
  milestone: number
  target: number
}

interface CanvassingDataForSelf extends CanvassingDataForChildren {
  historic: Timeline<number>
  milestones: Timeline<number>
}

export interface StructureWithCanvassing extends StructureWithCanvassingBase {
  canvassing: CanvassingDataForSelf
  parents: StructureBase[]
  children: StructureWithCanvassingBase[]
}

interface StructureReturns {
  stats: StructureWithCanvassing | undefined
  loading: boolean
}

interface StructureWithCanvassingBase extends StructureBase {
  canvassing: CanvassingDataForChildren
  counts: VDCounts
}

interface CanvassingResult {
  structure: StructureWithCanvassing
}

const query = gql`
query getCanvassingData($type: String!, $code: String!, $childType: String!, $filterLabel: String) {
  structure(type: $type, code: $code, filterLabel: $filterLabel) {
    type
    code
    name
    responsiblePersons { firstname surname displayname }
    userHasAccess
    structureHasAssignedWards
    counts {
      total
      filtered
    }
    canvassing {
        current
        last7days
        last30days
        weeklyAvg
        milestone
        target
        historic {
          date
          count
        }
        milestones {
          date
          count
        }
      }
    parents { type code name userHasAccess }
    children(type: $childType) {
      type
      code
      name
      responsiblePersons { firstname surname displayname }
      userHasAccess
      structureHasAssignedWards
      counts {
        total
        filtered
      }
      canvassing {
        current
        last7days
        last30days
        weeklyAvg
        milestone
        target
      }
    }
  }
}
`

export function useCanvassing ({ type, code, childType: ct, filterLabel }: StructParams): StructureReturns {
  const childType = ct ?? getChildType({ type, code, filterLabel })
  const { error, data, refetch, loading } = useQuery<CanvassingResult, QueryStructParams>(
    query, { variables: { type, code, childType, filterLabel }, notifyOnNetworkStatusChange: true }
  )

  useEffect(() => {
    refetch()
  }, [filterLabel, refetch])

  if (error?.graphQLErrors[0]?.extensions?.code === 'FORBIDDEN') {
    throw new ForbiddenError('this structure')
  }
  if (error != null) throw error

  return { stats: data?.structure, loading: loading }
}
